import { ReactNode, useEffect } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FacebookProvider } from 'react-facebook';
import { ToastContainer } from 'react-toastify';
import { useRouter } from 'next/router';

import { ReactQueryWrapper, ServiceProvider } from '@restworld/data-services';
import { AuthProvider, readStoredToken, useAuth } from '@restworld/feature-authentication';

import { GlobalOptionsFetchingProvider, UTMSourceProvider, ToastMessageContextProvider, AnalyticsProvider } from './';
import { IubendaProvider } from './iubenda';
import { defaultToastConfig } from '../constants/default-toast-config';
import { useIsEmployerApp } from '../hooks';
import { useIntercomBootProps } from '../hooks/use-intercom-boot-props';

const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL ?? '';
const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '';
const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';
const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';

export const MainContext = ({ children }: { children: React.ReactNode }) => (
  <ReactQueryWrapper>
    <AuthProvider baseUrl={baseUrl}>
      <ServiceProviderWrapper>
        <IubendaProvider>
          <GoogleOAuthProvider clientId={googleClientId}>
            <FacebookProvider appId={facebookAppId}>
              <IntercomServiceProvider>
                <AnalyticsProvider>
                  <UTMSourceProvider>
                    <GlobalOptionsFetchingProvider>
                      <ToastMessageContextProvider>{children}</ToastMessageContextProvider>
                      <ToastContainer {...defaultToastConfig} />
                    </GlobalOptionsFetchingProvider>
                  </UTMSourceProvider>
                </AnalyticsProvider>
              </IntercomServiceProvider>
            </FacebookProvider>
          </GoogleOAuthProvider>
        </IubendaProvider>
      </ServiceProviderWrapper>
    </AuthProvider>
  </ReactQueryWrapper>
);
export const IntercomServiceProvider = ({ children }: { children: ReactNode }) => (
  <IntercomProvider appId={INTERCOM_APP_ID} initializeDelay={500}>
    <IntercomInitiation>{children}</IntercomInitiation>
  </IntercomProvider>
);

const IntercomInitiation = ({ children }: { children?: ReactNode }) => {
  const { boot, shutdown } = useIntercom();
  const { user } = useAuth();
  const router = useRouter();
  const { isEmployerApp } = useIsEmployerApp();
  // The token travels inside `customAttributes` because react-use-intercom v5 predates
  // `intercom_user_jwt` and silently drops any boot prop it does not recognise, while
  // `customAttributes` is spread verbatim into the raw Intercom settings. See the helper for why
  // the id and the email are sent either inside the token or beside it, never both, and the hook
  // for why this value must not change identity on every navigation.
  const autoBootProps = useIntercomBootProps(user, isEmployerApp, router.pathname);

  useEffect(() => {
    if (typeof window !== 'undefined') boot({ ...autoBootProps });
    return () => {
      shutdown();
    };
  }, [autoBootProps, boot, shutdown]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
export const ServiceProviderWrapper = ({ children }: WrapperProps) => {
  const { jwt: token } = useAuth();
  // Two defects lived on the line this replaces, both fixed by going through `readStoredToken`:
  //
  // 1. `token || typeof localStorage !== 'undefined' ? … : ''` parses as
  //    `(token || typeof localStorage !== 'undefined') ? … : ''` — `?:` binds looser than `||`. So
  //    `token` was only ever a truthiness input and never the value: a signed-in user's in-memory
  //    token was discarded and storage re-read on every render.
  // 2. `typeof localStorage !== 'undefined'` cannot see a **null** `localStorage`, because
  //    `typeof null === 'object'`. An Android WebView with site data disabled exposes exactly that,
  //    so the guard passed and `.getItem` threw on null — crashing `ServiceProviderWrapper`, which
  //    wraps the whole app, so the entire page went to the ErrorBoundary (prod, 02/10/2026).
  //
  // `readStoredToken` already handles both the missing-global (SSR) and unavailable-storage cases.
  const jwt = token || readStoredToken() || '';
  return (
    <ServiceProvider baseUrl={baseUrl} jwt={jwt}>
      {children}
    </ServiceProvider>
  );
};
interface WrapperProps {
  children: ReactNode;
}
